import { type FC, type MouseEvent } from "react";

import { type CourseType } from "components/server-types";
import { Checkbox } from "mds/components/Checkbox";
import { ListItem } from "mds/components/ListItem";
import { FolderIcon } from "mds/icons";
import type { CourseGroupingWithCoursesType } from "store/selectors/types";
import { getCourseDisplayTitle } from "utils/course";

type CourseAdminSelectionListProps = {
  groupingsWithCourses: CourseGroupingWithCoursesType[];
  ungroupedCourses: CourseType[];
  selectedGroupingIds: string[];
  selectedCourseIds: string[];
  onGroupingToggle: (groupingId: string) => void;
  onCourseToggle: (courseId: string) => void;
};

export const CourseAdminSelectionList: FC<CourseAdminSelectionListProps> = ({
  groupingsWithCourses,
  ungroupedCourses,
  selectedGroupingIds,
  selectedCourseIds,
  onGroupingToggle,
  onCourseToggle,
}) => {
  const handleGroupingClick = (event: MouseEvent, groupingId: string) => {
    event.preventDefault();
    event.stopPropagation();

    onGroupingToggle(groupingId);
  };

  const handleCourseClick = (event: MouseEvent, courseId: string, disabled: boolean) => {
    event.preventDefault();
    event.stopPropagation();

    if (!disabled) {
      onCourseToggle(courseId);
    }
  };

  const selectedGroupingIdSet = new Set(selectedGroupingIds);
  const selectedCourseIdSet = new Set(selectedCourseIds);

  const isCourseChecked = (course: CourseType, groupingId: string | null) => {
    if (groupingId && selectedGroupingIdSet.has(groupingId)) {
      return true;
    }

    return selectedCourseIdSet.has(course.id);
  };

  if (groupingsWithCourses.length === 0 && ungroupedCourses.length === 0) {
    return null;
  }

  return (
    <div className="max-h-[240px] overflow-y-scroll rounded-lg border border-black-tint-90 p-2">
      {groupingsWithCourses.map((grouping) => {
        const groupingSelected = selectedGroupingIdSet.has(grouping.id);
        return (
          <div className="mb-2 last:mb-0" key={grouping.id}>
            <ListItem className="w-full gap-2">
              <Checkbox
                checked={groupingSelected}
                className="w-full"
                onContainerClick={(e) => handleGroupingClick(e, grouping.id)}
              >
                <span className="flex w-full items-center gap-2">
                  <FolderIcon className="shrink-0 text-black-tint-70" />
                  <span className="mt-0.5 font-medium">{grouping.title}</span>
                </span>
              </Checkbox>
            </ListItem>

            {grouping.courses.map((course) => {
              const checked = isCourseChecked(course, grouping.id);

              return (
                <div className="pl-6" key={course.id}>
                  <ListItem className="w-full gap-2">
                    <Checkbox
                      checked={checked}
                      className="w-full"
                      disabled={groupingSelected}
                      onContainerClick={(e) => handleCourseClick(e, course.id, groupingSelected)}
                    >
                      <span className="w-full font-medium">{getCourseDisplayTitle(course)}</span>
                    </Checkbox>
                  </ListItem>
                </div>
              );
            })}
          </div>
        );
      })}

      {ungroupedCourses.map((course) => (
        <div key={course.id}>
          <ListItem className="w-full gap-2">
            <Checkbox
              checked={selectedCourseIdSet.has(course.id)}
              className="w-full"
              onContainerClick={(e) => handleCourseClick(e, course.id, false)}
            >
              <span className="w-full font-medium">{getCourseDisplayTitle(course)}</span>
            </Checkbox>
          </ListItem>
        </div>
      ))}
    </div>
  );
};
